import React, { useState } from "react";
import { ReactComponent as LogoutIcon } from "../../assets/icons/svg/logout.svg";
import AvatarIcon from "../../assets/images/profile-avatar.png";
import Button from "../ui/buttons";
import { handleUserLogout } from "../../../src/utils/authHelpers"; 
import { useProfileImage } from "../../contexts/ProfileImageContext";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

function SettingsTopSection() {
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { profileImage } = useProfileImage();

  const handleLogoutClick = () => {
    setShowLogoutPopup(true);
  };

  /**
   * ✅ UPDATED: Use centralized handleUserLogout for consistent behavior
   * This ensures proper app domain redirect on logout
   */
  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);

    try {
      console.log("🚪 Starting logout process from Settings...");
      
      // ✅ Use the centralized logout function
      // It handles everything: flag setting, token clearing, and redirect
      await handleUserLogout();
      
      // Note: We won't reach here because handleUserLogout redirects the page
      console.log("✅ Logout completed");
      
    } catch (error) {
      console.error("❌ Logout error:", error);
      
      // Even on error, the centralized function handles redirect
      // Just log the error for debugging
      
    } finally {
      // These won't execute because page will redirect,
      // but keep them for edge cases where redirect might fail
      setIsLoggingOut(false);
      setShowLogoutPopup(false);
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutPopup(false);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="font-poppins font-semibold text-[32px] text-[#11142D] leading-none" style={{ fontFamily: DEFAULT_FONT_FAMILY }} >
              Settings
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogoutClick}
            disabled={isLoggingOut}
            className="flex items-center gap-2 text-sm 3xl:text-sm px-2 py-2 3xl:px-3 3xl:py-2  font-normal"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {isLoggingOut ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1E1F26]"></div>
            ) : (
              <>
                <LogoutIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
                <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Logout</span>
              </>
            )}
          </Button>

          <img
            className="h-10 w-10 3xl:h-12 3xl:w-12 rounded-full object-cover"
            src={profileImage}
            alt="profile picture"
          />
        </div>
      </div>

      {/* Logout Confirmation Popup */}
      {showLogoutPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mt-2 mb-2">
                <h3
                  className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Log out?
                </h3>
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-6" />

              {/* Description */}
              <p
                className="text-[#070707] text-sm 3xl:text-[16px] mb-6"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Are you sure you want to log out of your account?
              </p>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-4" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="cancel"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleCancelLogout}
                  disabled={isLoggingOut}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Stay signed in
                </Button>
                <Button
                  variant="danger"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleConfirmLogout}
                  disabled={isLoggingOut}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {isLoggingOut ? "Logging out..." : "Log out"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SettingsTopSection;